import { createRoot, hydrateRoot } from 'react-dom/client';
import { loadableReady } from '@loadable/component';
import { createBrowserHistory } from 'history';
import initRoute from 'helpers/initRoute';
import * as device from 'helpers/ua';
import createApi from 'modules/api';
import { initUserAgent } from 'helpers/userAgent';
import initReferer from 'helpers/referer';
import initLocation from 'helpers/location';
import createStore from 'store';
import App, { getMatched } from 'components/App';
import createCookies from 'helpers/cookies';
import { DOMAIN_PREFIX } from 'helpers/fieldOptions/domainPrefix';

import handleError from './handleError';

const API_ROUTE = `/${DOMAIN_PREFIX}/api`;

const api = createApi({
  formatUrl: ({ url }) => `${API_ROUTE}${url}`,
});

const history = createBrowserHistory();

const appNode = document.getElementById('app');
const withSSR = !!appNode.innerHTML;

const renderApp = (store) => {
  const app = <App store={store} history={history} device={device} />;

  if (withSSR) {
    hydrateRoot(appNode, app);
  } else {
    createRoot(appNode).render(app);
  }
};

export default (reducer, initialState) => {
  const initMatchedRoute = (params) => initRoute({ ...params, getMatched });

  // Lib issue: https://github.com/supasate/connected-react-router/issues/338
  if (
    typeof window !== 'undefined' &&
    initialState.router &&
    initialState.router.location
  ) {
    initialState.router.location.hash = window.location.hash;
  }

  const store = createStore({
    reducer,
    initialState,
    history,
    api,
    cookies: createCookies(),
    handleError,
    initMatchedRoute,
    referer: initReferer(),
    userAgent: initUserAgent(),
    location: initLocation(),
  });

  global.__store = store;

  const { dispatch } = store;

  history.listen(() => {
    store.setCurrentLocation(history.location);

    dispatch(initMatchedRoute());
  });

  const render = () => renderApp(store);

  loadableReady(() => {
    dispatch(
      initMatchedRoute({
        App,
        withFetchAction: !withSSR, // fetch action was called on server side
      }),
    ).then(render, render);
  });

  return { store };
};
